import styled from 'styled-components';
import {
  Link as MetLink,
  LinkProps,
  Spinner,
  ButtonProps,
} from '@ally/metronome-ui';
import { RouteValue } from '../../../types';
import { muiAllyTheme } from '../../../utils';
import { forwardRef, MouseEvent, Ref } from 'react';
import { useTransmit } from '../../../hooks';
import { Destination } from '@ally/transmitigator';

const StyledSpinner = styled(Spinner)`
  position: absolute;
  top: 20px;
  left: 44px;
  ${({ theme: { media } }): string => `
  ${media.down('sm')} {
    top: 4px;
    left: 30px;
  }
`}
`;

const StyledSpan = styled.span`
  position: relative;
`;
const BaseLink = styled(MetLink)`
  white-space: break-spaces;
  ${(props: CustomProps): string | undefined =>
    props.color && `color: ${props.color};`}
  ${(props: CustomProps): string | undefined =>
    props.fontSize && `font-size: ${props.fontSize}`}
`;

const StyledDisabledLink = styled(BaseLink)`
  pointer-events: none;
  color: ${muiAllyTheme.colors['gray-800']};
`;

// interface IProps extends LinkProps {}

// This enforces props.to to be type RouteValue if props.$internal is present on the element, if not or props.$internal=false, props.to is type string
// $internal must contain $ to satisfy styled-components as it is a transient property
type InternalLinkProps =
  | { $internal: true; to: RouteValue }
  | { $internal?: false; to: string };

type CustomProps = {
  isLoading?: boolean;
  color?: string;
  transmitdestination?: Destination;
  fontSize?: string;
  target?: string;
  download?: boolean | HTMLAnchorElement['download'];
  tabIndex?: number;
  id?: string;
};

type Props = Omit<LinkProps, 'href' | 'children'> &
  InternalLinkProps &
  ButtonProps &
  CustomProps;

const Link = forwardRef((props: Props, focusRef: Ref<HTMLAnchorElement>) => {
  const { leaveWithTicket } = useTransmit();

  const {
    isLoading,
    transmitdestination,
    onClick,
    to,
    unsupportedContent,
    ...restProps
  } = props;

  const handleClick =
    transmitdestination && transmitdestination !== 'wealth'
      ? (e: MouseEvent<Element>) => leaveWithTicket(e, to, transmitdestination)
      : (onClick as React.MouseEventHandler<Element> | undefined);

  const textProps = {
    ...restProps,
    text: isLoading ? '' : props.text ?? '',
    href: transmitdestination ? '#' : to,
    onClick: handleClick,
  };

  return (
    <StyledSpan>
      {props.isLoading && <StyledSpinner size='small' themeColor='light' />}
      {props.disabled ? (
        <StyledDisabledLink {...textProps} ref={focusRef} />
      ) : (
        <BaseLink {...textProps} ref={focusRef} />
      )}
    </StyledSpan>
  );
});

export default Link;
